<template>
  <div class="screen">
    <section v-if="screen === 'home'" class="stack">
      <div class="card">
        <h2>Customer Versions</h2>
        <div v-if="customerVersionBuckets.length" class="responsive-grid">
          <article
            v-for="bucket in customerVersionBuckets"
            :key="bucket.version"
            class="soft-card"
          >
            <h3>{{ bucket.version }}</h3>
            <p class="muted">{{ bucket.customers.length }} customers</p>
            <ul class="simple-list">
              <li v-for="customer in bucket.customers" :key="customer.id">
                {{ customer.name }}
              </li>
            </ul>
          </article>
        </div>
        <p v-else class="muted">No customers yet.</p>
      </div>

      <div class="card">
        <h2>Next Ring Deployment</h2>
        <template v-if="nextRingRecommendation">
          <div class="split">
            <div>
              <h3>{{ nextRingRecommendation.ring.name }}</h3>
              <p class="muted">
                {{
                  nextRingRecommendation.ring.description || "No description"
                }}
              </p>
            </div>
            <span
              class="badge"
              :style="{ backgroundColor: nextRingRecommendation.ring.color }"
            >
              Order {{ nextRingRecommendation.ring.order }}
            </span>
          </div>
          <table class="data-table">
            <thead>
              <tr>
                <th>Customer</th>
                <th>Current Version</th>
                <th>Days Since Last Deploy</th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="customer in nextRingRecommendation.customers"
                :key="customer.id"
              >
                <td>{{ customer.name }}</td>
                <td>{{ customer.currentVersion || "Unknown" }}</td>
                <td>{{ customer.daysSinceDeploy }}</td>
              </tr>
            </tbody>
          </table>
        </template>
        <p v-else class="muted">No rings with customers are available yet.</p>
      </div>

      <div class="card">
        <h2>Upcoming Planned Deploys</h2>
        <table v-if="upcomingPlanned.length" class="data-table">
          <thead>
            <tr>
              <th>Customer</th>
              <th>Versions</th>
              <th>Planned Date</th>
              <th>Assigned User</th>
              <th>Notes</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="planned in upcomingPlanned" :key="planned.id">
              <td>{{ planned.customerName }}</td>
              <td>
                {{ planned.currentVersion }} -> {{ planned.targetVersion }}
              </td>
              <td>{{ planned.plannedDate }}</td>
              <td>{{ getUserLabel(planned.assignedUserId) }}</td>
              <td>{{ planned.notes || "—" }}</td>
            </tr>
          </tbody>
        </table>
        <p v-else class="muted">No planned deployments are scheduled.</p>
      </div>
    </section>

    <section v-else-if="screen === 'users'" class="two-column">
      <form class="card stack" @submit.prevent="submitUser">
        <div class="split">
          <h2>{{ userForm.id ? "Edit User" : "Create User" }}</h2>
          <button
            v-if="userForm.id"
            type="button"
            class="ghost"
            @click="resetUserForm"
          >
            Clear
          </button>
        </div>
        <label>
          First name
          <input v-model.trim="userForm.name" required />
        </label>
        <label>
          Surname
          <input v-model.trim="userForm.surname" required />
        </label>
        <label>
          Email
          <input v-model.trim="userForm.email" type="email" required />
        </label>
        <button type="submit">
          {{ userForm.id ? "Update User" : "Save User" }}
        </button>
      </form>

      <div class="card">
        <h2>Users</h2>
        <table class="data-table">
          <thead>
            <tr>
              <th>Name</th>
              <th>Email</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="user in sortedUsers" :key="user.id">
              <td>{{ user.name }} {{ user.surname }}</td>
              <td>{{ user.email }}</td>
              <td class="actions">
                <button type="button" class="ghost" @click="editUser(user)">
                  Edit
                </button>
                <button
                  type="button"
                  class="danger"
                  @click="removeUser(user.id)"
                >
                  Delete
                </button>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </section>

    <section v-else-if="screen === 'deploy-units'" class="two-column">
      <form class="card stack" @submit.prevent="submitDeployUnit">
        <div class="split">
          <h2>
            {{ deployUnitForm.id ? "Edit Deploy Unit" : "Create Deploy Unit" }}
          </h2>
          <button
            v-if="deployUnitForm.id"
            type="button"
            class="ghost"
            @click="resetDeployUnitForm"
          >
            Clear
          </button>
        </div>
        <label>
          Name
          <input v-model.trim="deployUnitForm.name" required />
        </label>
        <label>
          Description
          <textarea v-model.trim="deployUnitForm.description" rows="5" />
        </label>
        <button type="submit">
          {{ deployUnitForm.id ? "Update Deploy Unit" : "Save Deploy Unit" }}
        </button>
      </form>

      <div class="card">
        <h2>Deploy Units</h2>
        <table class="data-table">
          <thead>
            <tr>
              <th>Name</th>
              <th>Description</th>
              <th>Created</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="unit in sortedDeployUnits" :key="unit.id">
              <td>{{ unit.name }}</td>
              <td>{{ unit.description || "—" }}</td>
              <td>{{ unit.createdAt.slice(0, 10) }}</td>
              <td class="actions">
                <button
                  type="button"
                  class="ghost"
                  @click="editDeployUnit(unit)"
                >
                  Edit
                </button>
                <button
                  type="button"
                  class="danger"
                  @click="removeDeployUnit(unit.id)"
                >
                  Delete
                </button>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </section>

    <section v-else-if="screen === 'deploy-steps'" class="stack">
      <div class="three-column">
        <form class="card stack" @submit.prevent="submitSection">
          <div class="split">
            <h2>{{ sectionForm.id ? "Edit Section" : "Create Section" }}</h2>
            <button
              v-if="sectionForm.id"
              type="button"
              class="ghost"
              @click="resetSectionForm"
            >
              Clear
            </button>
          </div>
          <label>
            Name
            <input v-model.trim="sectionForm.name" required />
          </label>
          <label>
            Deploy Unit
            <select v-model="sectionForm.deployUnitId" required>
              <option value="">Select deploy unit</option>
              <option
                v-for="unit in sortedDeployUnits"
                :key="unit.id"
                :value="unit.id"
              >
                {{ unit.name }}
              </option>
            </select>
          </label>
          <label>
            Position
            <input
              v-model.number="sectionForm.position"
              type="number"
              min="1"
              required
            />
          </label>
          <button type="submit">
            {{ sectionForm.id ? "Update Section" : "Save Section" }}
          </button>
        </form>

        <form class="card stack" @submit.prevent="submitStep">
          <div class="split">
            <h2>{{ stepForm.id ? "Edit Step" : "Create Step" }}</h2>
            <button
              v-if="stepForm.id"
              type="button"
              class="ghost"
              @click="resetStepForm"
            >
              Clear
            </button>
          </div>
          <label>
            Name
            <input v-model.trim="stepForm.name" required />
          </label>
          <label>
            Description
            <textarea v-model.trim="stepForm.description" rows="4" />
          </label>
          <label>
            Section
            <select v-model="stepForm.sectionId" required>
              <option value="">Select section</option>
              <option
                v-for="section in sortedSections"
                :key="section.id"
                :value="section.id"
              >
                {{ getDeployUnitLabel(section.deployUnitId) }} /
                {{ section.name }}
              </option>
            </select>
          </label>
          <label>
            Position
            <input
              v-model.number="stepForm.position"
              type="number"
              min="1"
              required
            />
          </label>
          <button type="submit">
            {{ stepForm.id ? "Update Step" : "Save Step" }}
          </button>
        </form>

        <div class="card stack">
          <h2>Library Guidance</h2>
          <p class="muted">
            Use the buttons in the library panels to create sections directly in
            a deploy unit, create steps directly in a section, or insert a step
            immediately after an existing step.
          </p>
        </div>
      </div>

      <div class="card">
        <h2>Deploy Step Library</h2>
        <div v-if="sortedDeployUnits.length" class="stack">
          <details
            v-for="unit in sortedDeployUnits"
            :key="unit.id"
            open
            class="details-panel"
          >
            <summary>
              <span>{{ unit.name }}</span>
              <button
                type="button"
                class="ghost"
                @click.prevent="primeSectionForUnit(unit.id)"
              >
                Add Section
              </button>
            </summary>
            <div class="stack">
              <details
                v-for="section in getSectionsForUnit(unit.id)"
                :key="section.id"
                open
                class="details-panel details-panel--nested"
              >
                <summary>
                  <span>{{ section.position }}. {{ section.name }}</span>
                  <span class="actions">
                    <button
                      type="button"
                      class="ghost"
                      @click.prevent="editSection(section)"
                    >
                      Edit
                    </button>
                    <button
                      type="button"
                      class="ghost"
                      @click.prevent="primeStepForSection(section.id)"
                    >
                      Add Step
                    </button>
                    <button
                      type="button"
                      class="danger"
                      @click.prevent="removeSection(section.id)"
                    >
                      Delete
                    </button>
                  </span>
                </summary>
                <ul class="simple-list">
                  <li
                    v-for="step in getStepsForSection(section.id)"
                    :key="step.id"
                  >
                    <div>
                      <strong>{{ step.position }}. {{ step.name }}</strong>
                      <p class="muted">
                        {{ step.description || "No description" }}
                      </p>
                    </div>
                    <span class="actions">
                      <button
                        type="button"
                        class="ghost"
                        @click="editStep(step)"
                      >
                        Edit
                      </button>
                      <button
                        type="button"
                        class="ghost"
                        @click="primeStepAfter(step)"
                      >
                        Add After
                      </button>
                      <button
                        type="button"
                        class="danger"
                        @click="removeStep(step.id)"
                      >
                        Delete
                      </button>
                    </span>
                  </li>
                </ul>
              </details>
            </div>
          </details>
        </div>
        <p v-else class="muted">Create deploy units first.</p>
      </div>
    </section>

    <section v-else-if="screen === 'rings'" class="two-column">
      <div class="stack">
        <form class="card stack" @submit.prevent="submitRing">
          <div class="split">
            <h2>{{ ringForm.id ? "Edit Ring" : "Create Ring" }}</h2>
            <button
              v-if="ringForm.id"
              type="button"
              class="ghost"
              @click="resetRingForm"
            >
              Clear
            </button>
          </div>
          <label>
            Name
            <input v-model.trim="ringForm.name" required />
          </label>
          <label>
            Description
            <textarea v-model.trim="ringForm.description" rows="4" />
          </label>
          <label>
            Color
            <input v-model="ringForm.color" type="color" required />
          </label>
          <label>
            Order
            <input
              v-model.number="ringForm.order"
              type="number"
              min="1"
              required
            />
          </label>
          <button type="submit">
            {{ ringForm.id ? "Update Ring" : "Save Ring" }}
          </button>
        </form>

        <div class="responsive-grid">
          <article v-for="ring in orderedRings" :key="ring.id" class="card">
            <div class="split">
              <div>
                <h2>{{ ring.name }}</h2>
                <p class="muted">{{ ring.description || "No description" }}</p>
              </div>
              <span
                class="color-dot"
                :style="{ backgroundColor: ring.color }"
              />
            </div>
            <p class="muted">
              {{ getCustomersForRing(ring.id).length }} assigned customers
            </p>
            <div class="actions">
              <button type="button" class="ghost" @click="editRing(ring)">
                Edit
              </button>
              <button type="button" class="danger" @click="removeRing(ring.id)">
                Delete
              </button>
              <button
                type="button"
                class="ghost"
                @click="openRingAssignment(ring.id)"
              >
                Assign Customers
              </button>
            </div>
            <div class="badge-list">
              <span
                v-for="customer in getCustomersForRing(ring.id)"
                :key="customer.id"
                class="badge-list__item"
              >
                {{ customer.name }}
                <button
                  type="button"
                  @click="removeCustomerFromRing(customer.id)"
                >
                  x
                </button>
              </span>
            </div>
            <div
              v-if="ringAssignmentTargetId === ring.id"
              class="stack assignment-box"
            >
              <label
                v-for="customer in sortedCustomers"
                :key="customer.id"
                class="checkbox-row"
              >
                <input
                  v-model="ringAssignmentCustomerIds"
                  type="checkbox"
                  :value="customer.id"
                />
                <span>
                  {{ customer.name }}
                  <small class="muted"
                    >({{ getRingLabel(customer.ringId) }})</small
                  >
                </span>
              </label>
              <div class="actions">
                <button type="button" @click="applyRingAssignment(ring.id)">
                  Confirm Assignment
                </button>
                <button
                  type="button"
                  class="ghost"
                  @click="cancelRingAssignment"
                >
                  Close
                </button>
              </div>
            </div>
          </article>
        </div>
      </div>

      <div class="card stack">
        <h2>Ring Order</h2>
        <div
          v-for="ring in orderedRings"
          :key="`order-${ring.id}`"
          class="split soft-card"
        >
          <span>{{ ring.order }}. {{ ring.name }}</span>
          <span class="actions">
            <button type="button" class="ghost" @click="moveRing(ring.id, -1)">
              Up
            </button>
            <button type="button" class="ghost" @click="moveRing(ring.id, 1)">
              Down
            </button>
          </span>
        </div>
      </div>
    </section>

    <section v-else-if="screen === 'customers'" class="stack">
      <div class="two-column">
        <form class="card stack" @submit.prevent="submitCustomer">
          <div class="split">
            <h2>{{ customerForm.id ? "Edit Customer" : "Create Customer" }}</h2>
            <button
              v-if="customerForm.id"
              type="button"
              class="ghost"
              @click="resetCustomerForm"
            >
              Clear
            </button>
          </div>
          <label>
            Name
            <input v-model.trim="customerForm.name" required />
          </label>
          <label>
            Current Version
            <input v-model.trim="customerForm.currentVersion" />
          </label>
          <label>
            Last Deploy Date
            <input v-model="customerForm.lastDeployDate" type="date" />
          </label>
          <label>
            Deployed By
            <input v-model.trim="customerForm.deployedBy" />
          </label>
          <label>
            Ring Assignment
            <select v-model="customerForm.ringId">
              <option value="">Unassigned</option>
              <option
                v-for="ring in orderedRings"
                :key="ring.id"
                :value="ring.id"
              >
                {{ ring.name }}
              </option>
            </select>
          </label>
          <label>
            Deploy Units
            <select v-model="customerForm.deployUnitIds" multiple size="6">
              <option
                v-for="unit in sortedDeployUnits"
                :key="unit.id"
                :value="unit.id"
              >
                {{ unit.name }}
              </option>
            </select>
          </label>
          <label>
            Live Date
            <input v-model="customerForm.liveDate" type="date" />
          </label>
          <label>
            DB Valid Date
            <input v-model="customerForm.dbValidDate" type="date" />
          </label>
          <button type="submit">
            {{ customerForm.id ? "Update Customer" : "Save Customer" }}
          </button>
        </form>

        <div class="card stack">
          <h2>Customer Search</h2>
          <label>
            Search
            <input
              v-model.trim="customerSearch"
              placeholder="Search by customer, version, deploying user, or ring"
            />
          </label>
        </div>
      </div>

      <div class="card">
        <h2>Customers Overview</h2>
        <table class="data-table">
          <thead>
            <tr>
              <th>Customer</th>
              <th>Ring</th>
              <th>Deploy Units</th>
              <th>Current Version</th>
              <th>Last Deploy</th>
              <th>Live Date</th>
              <th>DB Valid Date</th>
              <th>Deployed By</th>
              <th>Deploy Count</th>
              <th>Status</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="row in customerRows" :key="row.key">
              <td>{{ row.customerName }}</td>
              <td>{{ row.ringName }}</td>
              <td>{{ row.deployUnits.join(", ") || "—" }}</td>
              <td>{{ row.currentVersion || "Unknown" }}</td>
              <td>{{ row.lastDeployDate || "Never" }}</td>
              <td>{{ row.liveDate || "—" }}</td>
              <td>{{ row.dbValidDate || "—" }}</td>
              <td>{{ row.deployedBy || "—" }}</td>
              <td>{{ row.totalDeployCount }}</td>
              <td>{{ row.latestDeployStatus }}</td>
              <td class="actions">
                <button
                  v-if="row.customer"
                  type="button"
                  class="ghost"
                  @click="editCustomer(row.customer)"
                >
                  Edit
                </button>
                <button
                  v-if="row.customer"
                  type="button"
                  class="danger"
                  @click="removeCustomer(row.customer.id)"
                >
                  Delete
                </button>
                <button
                  v-if="row.latestDeploymentId"
                  type="button"
                  class="ghost"
                  @click="openHistoryDeployment(row.latestDeploymentId)"
                >
                  Open Latest Deploy
                </button>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </section>

    <section v-else-if="screen === 'notes'" class="two-column">
      <form class="card stack" @submit.prevent="submitNote">
        <div class="split">
          <h2>{{ noteForm.id ? "Edit Note" : "Create Note" }}</h2>
          <button
            v-if="noteForm.id"
            type="button"
            class="ghost"
            @click="resetNoteForm"
          >
            Clear
          </button>
        </div>
        <label>
          Title
          <input v-model.trim="noteForm.title" required />
        </label>
        <label>
          Content
          <textarea v-model.trim="noteForm.content" rows="6" required />
        </label>
        <label>
          Customer Name
          <input v-model.trim="noteForm.customerName" list="customer-names" />
        </label>
        <label>
          Target Version
          <input v-model.trim="noteForm.targetVersion" />
        </label>
        <label>
          Deploy Unit
          <select v-model="noteForm.deployUnitId">
            <option value="">All deploy units</option>
            <option
              v-for="unit in sortedDeployUnits"
              :key="unit.id"
              :value="unit.id"
            >
              {{ unit.name }}
            </option>
          </select>
        </label>
        <button type="submit">
          {{ noteForm.id ? "Update Note" : "Save Note" }}
        </button>
        <datalist id="customer-names">
          <option
            v-for="customer in sortedCustomers"
            :key="customer.id"
            :value="customer.name"
          />
        </datalist>
      </form>

      <div class="card stack">
        <h2>Future Deploy Notes</h2>
        <article
          v-for="note in sortedNotes"
          :key="note.id"
          class="soft-card stack"
        >
          <div class="split">
            <h3>{{ note.title }}</h3>
            <span class="actions">
              <button type="button" class="ghost" @click="editNote(note)">
                Edit
              </button>
              <button type="button" class="danger" @click="removeNote(note.id)">
                Delete
              </button>
            </span>
          </div>
          <p>{{ note.content }}</p>
          <div class="badge-list">
            <span
              v-if="
                !note.customerName && !note.targetVersion && !note.deployUnitId
              "
              class="badge-list__item"
            >
              Applies everywhere
            </span>
            <span v-if="note.customerName" class="badge-list__item">
              Customer: {{ note.customerName }}
            </span>
            <span v-if="note.targetVersion" class="badge-list__item">
              Version: {{ note.targetVersion }}
            </span>
            <span v-if="note.deployUnitId" class="badge-list__item">
              Unit: {{ getDeployUnitLabel(note.deployUnitId) }}
            </span>
          </div>
        </article>
      </div>
    </section>

    <section v-else-if="screen === 'planned'" class="stack">
      <div class="two-column">
        <form class="card stack" @submit.prevent="submitPlannedDeploy">
          <div class="split">
            <h2>
              {{
                plannedForm.id ? "Edit Planned Deploy" : "Create Planned Deploy"
              }}
            </h2>
            <button
              v-if="plannedForm.id"
              type="button"
              class="ghost"
              @click="resetPlannedForm"
            >
              Clear
            </button>
          </div>
          <label>
            Customer
            <select
              v-model="plannedForm.customerName"
              required
              @change="syncPlannedCurrentVersion"
            >
              <option value="">Select customer</option>
              <option
                v-for="customer in sortedCustomers"
                :key="customer.id"
                :value="customer.name"
              >
                {{ customer.name }}
              </option>
            </select>
          </label>
          <label>
            Current Version
            <input v-model.trim="plannedForm.currentVersion" />
          </label>
          <label>
            Target Version
            <input v-model.trim="plannedForm.targetVersion" required />
          </label>
          <label>
            Planned Date
            <input v-model="plannedForm.plannedDate" type="date" required />
          </label>
          <label>
            Assigned User
            <select v-model="plannedForm.assignedUserId">
              <option value="">Unassigned</option>
              <option
                v-for="user in sortedUsers"
                :key="user.id"
                :value="user.id"
              >
                {{ user.name }} {{ user.surname }}
              </option>
            </select>
          </label>
          <label>
            Notes
            <textarea v-model.trim="plannedForm.notes" rows="4" />
          </label>
          <label>
            Status
            <select v-model="plannedForm.status">
              <option value="planned">planned</option>
              <option value="in-progress">in-progress</option>
              <option value="completed">completed</option>
              <option value="cancelled">cancelled</option>
            </select>
          </label>
          <button type="submit">
            {{
              plannedForm.id ? "Update Planned Deploy" : "Save Planned Deploy"
            }}
          </button>
        </form>

        <div v-if="activeReminderPlanned" class="card stack">
          <h2>Reminder Simulator</h2>
          <p>
            {{ activeReminderPlanned.customerName }} /
            {{ activeReminderPlanned.targetVersion }}
          </p>
          <label>
            Recipients
            <select v-model="reminderUserIds" multiple size="6">
              <option
                v-for="user in sortedUsers"
                :key="user.id"
                :value="user.id"
              >
                {{ user.name }} {{ user.surname }}
              </option>
            </select>
          </label>
          <label>
            Additional Emails
            <textarea
              v-model.trim="reminderExtraEmails"
              rows="3"
              placeholder="one@example.com, two@example.com"
            />
          </label>
          <label>
            Message Body
            <textarea v-model.trim="reminderMessage" rows="5" />
          </label>
          <div class="actions">
            <button type="button" @click="sendReminder">Send Reminder</button>
            <button type="button" class="ghost" @click="closeReminder">
              Close
            </button>
          </div>
        </div>
      </div>

      <div class="card">
        <div class="split">
          <h2>Planned Deploys</h2>
          <p class="muted">Sorted by planned date, undated last.</p>
        </div>
        <table class="data-table">
          <thead>
            <tr>
              <th>Customer</th>
              <th>Current</th>
              <th>Target</th>
              <th>Date</th>
              <th>Assigned</th>
              <th>Status</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="planned in sortedPlannedDeploys" :key="planned.id">
              <td>{{ planned.customerName }}</td>
              <td>{{ planned.currentVersion || "—" }}</td>
              <td>{{ planned.targetVersion }}</td>
              <td>{{ planned.plannedDate || "Undated" }}</td>
              <td>
                {{
                  planned.assignedUserId
                    ? getUserLabel(planned.assignedUserId)
                    : "Unassigned"
                }}
              </td>
              <td>
                <span class="status-pill">{{ planned.status }}</span>
              </td>
              <td class="actions">
                <button
                  type="button"
                  class="ghost"
                  @click="editPlannedDeploy(planned)"
                >
                  Edit
                </button>
                <button
                  type="button"
                  class="ghost"
                  @click="openReminder(planned.id)"
                >
                  Reminder
                </button>
                <button
                  type="button"
                  class="ghost"
                  @click="markPlannedCompleted(planned.id)"
                >
                  Complete
                </button>
                <button
                  type="button"
                  class="ghost"
                  @click="markPlannedCancelled(planned.id)"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  class="ghost"
                  @click="reopenPlanned(planned.id)"
                >
                  Reopen
                </button>
                <button type="button" @click="startPlanned(planned.id)">
                  Start
                </button>
                <button
                  type="button"
                  class="danger"
                  @click="removePlannedDeploy(planned.id)"
                >
                  Delete
                </button>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </section>

    <section v-else-if="screen === 'deploy'" class="stack">
      <div v-if="activeDeployment" class="stack">
        <div class="card">
          <div class="split">
            <div>
              <h2>{{ activeDeployment.customerName }}</h2>
              <p class="muted">
                {{ activeDeployment.currentVersion }} ->
                {{ activeDeployment.targetVersion }}
              </p>
            </div>
            <div class="actions">
              <button
                type="button"
                class="ghost"
                @click="leaveActiveDeployment"
              >
                Back to Form
              </button>
              <button
                type="button"
                @click="printDeployment(activeDeployment.id)"
              >
                Print / PDF
              </button>
            </div>
          </div>
        </div>

        <div class="card split">
          <div>
            <h3>Progress</h3>
            <p class="muted">
              {{ activeDeploymentProgress.completed }} /
              {{ activeDeploymentProgress.total }}
              steps completed
            </p>
          </div>
          <strong>{{ activeDeploymentProgress.percentage }}%</strong>
        </div>

        <p
          v-if="activeDeploymentProgress.status === 'completed'"
          class="success-callout"
        >
          All deployment steps are completed.
        </p>

        <div v-if="!activeDeploymentGroups.length" class="card">
          <p>This deployment has zero applicable steps.</p>
        </div>

        <div
          v-for="group in activeDeploymentGroups"
          :key="group.key"
          class="card stack"
        >
          <div class="split">
            <h3>{{ group.unitName }}</h3>
            <span class="muted"
              >{{ getUnitNotesForDeployment(group.key).length }} matching
              notes</span
            >
          </div>
          <article
            v-for="note in getUnitNotesForDeployment(group.key)"
            :key="note.id"
            class="soft-card note-card"
          >
            <strong>{{ note.title }}</strong>
            <p>{{ note.content }}</p>
          </article>
          <details
            v-for="section in group.sections"
            :key="section.key"
            open
            class="details-panel"
          >
            <summary>{{ section.sectionName }}</summary>
            <div
              v-for="step in section.steps"
              :key="step.state.stepId"
              class="step-row"
            >
              <label class="checkbox-row">
                <input
                  :checked="step.state.completed"
                  type="checkbox"
                  @change="
                    toggleDeploymentStep(
                      activeDeployment.id,
                      step.state.stepId,
                      eventChecked($event)
                    )
                  "
                />
                <span>
                  <strong>{{ step.stepName }}</strong>
                  <small v-if="step.stepDescription" class="muted">{{
                    step.stepDescription
                  }}</small>
                </span>
              </label>
              <textarea
                :value="step.state.notes"
                rows="3"
                placeholder="Step notes"
                @input="
                  updateDeploymentStepNotes(
                    activeDeployment.id,
                    step.state.stepId,
                    eventValue($event)
                  )
                "
              />
            </div>
          </details>
        </div>
      </div>

      <div v-else class="two-column">
        <form class="card stack" @submit.prevent="submitDeployment">
          <h2>Start New Deployment</h2>
          <div class="soft-card stack">
            <strong>Preconditions</strong>
            <p class="muted">Users: {{ state.data.users.length }}</p>
            <p class="muted">Customers: {{ state.data.customers.length }}</p>
            <p class="muted">Steps: {{ state.data.steps.length }}</p>
          </div>
          <label>
            Customer
            <select
              v-model="deployForm.customerId"
              required
              @change="syncDeployCurrentVersion"
            >
              <option value="">Select customer</option>
              <option
                v-for="customer in sortedCustomers"
                :key="customer.id"
                :value="customer.id"
              >
                {{ customer.name }}
              </option>
            </select>
          </label>
          <label>
            Current Version
            <input v-model.trim="deployForm.currentVersion" required />
          </label>
          <label>
            Target Version
            <input v-model.trim="deployForm.targetVersion" required />
          </label>
          <label>
            Deploying User
            <select v-model="deployForm.userId" required>
              <option value="">Select user</option>
              <option
                v-for="user in sortedUsers"
                :key="user.id"
                :value="user.id"
              >
                {{ user.name }} {{ user.surname }}
              </option>
            </select>
          </label>
          <label>
            Branch
            <select v-model="deployForm.branchId">
              <option value="">No branch</option>
              <option
                v-for="branch in sortedBranches"
                :key="branch.id"
                :value="branch.id"
              >
                {{ branch.name }}
              </option>
            </select>
          </label>
          <p class="muted">Applicable steps: {{ deployPreviewStepCount }}</p>
          <button type="submit" :disabled="!canStartDeployment">
            Start Deployment
          </button>
        </form>

        <div class="card stack">
          <h2>Matching Notes</h2>
          <article
            v-for="note in deployStartNotes"
            :key="note.id"
            class="soft-card note-card"
          >
            <strong>{{ note.title }}</strong>
            <p>{{ note.content }}</p>
          </article>
          <p v-if="!deployStartNotes.length" class="muted">
            No matching notes.
          </p>
        </div>
      </div>
    </section>

    <section v-else-if="screen === 'history'" class="stack">
      <div class="two-column">
        <div class="card stack">
          <h2>Search and Filter</h2>
          <label>
            Search
            <input
              v-model.trim="historySearch"
              placeholder="Search by customer, version, target version, or user"
            />
          </label>
          <label>
            Status
            <select v-model="historyStatusFilter">
              <option value="all">all</option>
              <option value="completed">completed</option>
              <option value="in-progress">in-progress</option>
              <option value="not-started">not-started</option>
            </select>
          </label>
          <button type="button" @click="exportFilteredHistoryCsv">
            Export Filtered CSV
          </button>
        </div>

        <div v-if="selectedHistoryDeployment" class="card stack">
          <div class="split">
            <h2>Deployment Detail</h2>
            <span class="status-pill">{{
              getProgress(selectedHistoryDeployment).status
            }}</span>
          </div>
          <p>
            <strong>Customer:</strong>
            {{ selectedHistoryDeployment.customerName }}
          </p>
          <p>
            <strong>Versions:</strong>
            {{ selectedHistoryDeployment.currentVersion }} ->
            {{ selectedHistoryDeployment.targetVersion }}
          </p>
          <p>
            <strong>User:</strong>
            {{ getUserLabel(selectedHistoryDeployment.userId) }}
          </p>
          <p>
            <strong>Date:</strong> {{ selectedHistoryDeployment.createdAt }}
          </p>
          <div class="actions">
            <button
              type="button"
              class="ghost"
              @click="resumeDeployment(selectedHistoryDeployment.id)"
            >
              Resume
            </button>
            <button
              type="button"
              class="ghost"
              @click="exportDeploymentCsv(selectedHistoryDeployment.id)"
            >
              Export CSV
            </button>
            <button
              type="button"
              @click="printDeployment(selectedHistoryDeployment.id)"
            >
              Print / PDF
            </button>
          </div>
          <div
            v-if="getDeploymentGroups(selectedHistoryDeployment).length"
            class="stack"
          >
            <details
              v-for="group in getDeploymentGroups(selectedHistoryDeployment)"
              :key="group.key"
              open
              class="details-panel"
            >
              <summary>{{ group.unitName }}</summary>
              <div
                v-for="section in group.sections"
                :key="section.key"
                class="stack"
              >
                <h3>{{ section.sectionName }}</h3>
                <div
                  v-for="step in section.steps"
                  :key="step.state.stepId"
                  class="soft-card"
                >
                  <strong>{{ step.stepName }}</strong>
                  <p class="muted">
                    {{ step.state.completed ? "Completed" : "Pending" }}
                  </p>
                  <p>{{ step.state.notes || "No notes" }}</p>
                </div>
              </div>
            </details>
          </div>
          <p v-else class="muted">This deployment has no steps.</p>
        </div>
      </div>

      <div class="card">
        <h2>Deployments</h2>
        <table class="data-table">
          <thead>
            <tr>
              <th>Customer</th>
              <th>Current</th>
              <th>Target</th>
              <th>User</th>
              <th>Date</th>
              <th>Progress</th>
              <th>Status</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="deployment in filteredHistoryRows" :key="deployment.id">
              <td>{{ deployment.customerName }}</td>
              <td>{{ deployment.currentVersion }}</td>
              <td>{{ deployment.targetVersion }}</td>
              <td>{{ getUserLabel(deployment.userId) }}</td>
              <td>{{ deployment.createdAt.slice(0, 10) }}</td>
              <td>
                {{ getProgress(deployment).completed }}/{{
                  getProgress(deployment).total
                }}
                ({{ getProgress(deployment).percentage }}%)
              </td>
              <td>{{ getProgress(deployment).status }}</td>
              <td class="actions">
                <button
                  type="button"
                  class="ghost"
                  @click="selectHistoryDeployment(deployment.id)"
                >
                  Inspect
                </button>
                <button
                  type="button"
                  class="ghost"
                  @click="resumeDeployment(deployment.id)"
                >
                  Resume
                </button>
                <button
                  type="button"
                  class="ghost"
                  @click="exportDeploymentCsv(deployment.id)"
                >
                  CSV
                </button>
                <button type="button" @click="printDeployment(deployment.id)">
                  Print
                </button>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </section>

    <section v-else-if="screen === 'branches'" class="two-column">
      <form class="card stack" @submit.prevent="submitBranch">
        <div class="split">
          <h2>{{ branchForm.id ? "Edit Branch" : "Create Branch" }}</h2>
          <button
            v-if="branchForm.id"
            type="button"
            class="ghost"
            @click="resetBranchForm"
          >
            Clear
          </button>
        </div>
        <label>
          Branch Name
          <input v-model.trim="branchForm.name" required />
        </label>
        <label>
          Starting Iteration
          <input
            v-model.number="branchForm.startingIteration"
            type="number"
            min="0"
            required
          />
        </label>
        <button type="submit">
          {{ branchForm.id ? "Update Branch" : "Save Branch" }}
        </button>
      </form>

      <div class="card">
        <h2>Branches</h2>
        <table class="data-table">
          <thead>
            <tr>
              <th>Name</th>
              <th>Starting</th>
              <th>Prod</th>
              <th>Dev</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="branch in sortedBranches" :key="branch.id">
              <td>{{ branch.name }}</td>
              <td>{{ branch.startingIteration }}</td>
              <td>{{ branch.prodIteration }}</td>
              <td>{{ branch.devIteration }}</td>
              <td class="actions">
                <button type="button" class="ghost" @click="editBranch(branch)">
                  Edit
                </button>
                <button
                  type="button"
                  class="danger"
                  @click="removeBranch(branch.id)"
                >
                  Delete
                </button>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </section>

    <section v-else-if="screen === 'applications'" class="stack">
      <div class="two-column">
        <form class="card stack" @submit.prevent="submitApplication">
          <h2>Create Application</h2>
          <label>
            Name
            <input v-model.trim="applicationName" required />
          </label>
          <button type="submit">Save Application</button>
        </form>

        <form class="card stack" @submit.prevent="submitApplicationProperty">
          <div class="split">
            <h2>
              {{
                applicationPropertyForm.id ? "Edit Property" : "Create Property"
              }}
            </h2>
            <button
              v-if="applicationPropertyForm.id"
              type="button"
              class="ghost"
              @click="resetApplicationPropertyForm"
            >
              Clear
            </button>
          </div>
          <label>
            Name
            <input v-model.trim="applicationPropertyForm.name" required />
          </label>
          <label>
            Type
            <select v-model="applicationPropertyForm.type" required>
              <option value="text">text</option>
              <option value="number">number</option>
              <option value="date">date</option>
              <option value="boolean">boolean</option>
            </select>
          </label>
          <button type="submit">
            {{
              applicationPropertyForm.id ? "Update Property" : "Save Property"
            }}
          </button>
        </form>
      </div>

      <div class="card">
        <h2>Applications</h2>
        <table class="data-table">
          <thead>
            <tr>
              <th>Application</th>
              <th
                v-for="property in sortedApplicationProperties"
                :key="property.id"
              >
                {{ property.name }}
              </th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="application in sortedApplications" :key="application.id">
              <td>
                <input
                  :value="application.name"
                  @input="renameApplication(application.id, eventValue($event))"
                />
              </td>
              <td
                v-for="property in sortedApplicationProperties"
                :key="`${application.id}-${property.id}`"
              >
                <input
                  v-if="property.type === 'text'"
                  :value="String(application.propertyValues[property.id] ?? '')"
                  @input="
                    setApplicationValue(
                      application.id,
                      property.id,
                      eventValue($event)
                    )
                  "
                />
                <input
                  v-else-if="property.type === 'number'"
                  :value="String(application.propertyValues[property.id] ?? '')"
                  type="number"
                  @input="
                    setApplicationValue(
                      application.id,
                      property.id,
                      eventValue($event)
                    )
                  "
                />
                <input
                  v-else-if="property.type === 'date'"
                  :value="String(application.propertyValues[property.id] ?? '')"
                  type="date"
                  @input="
                    setApplicationValue(
                      application.id,
                      property.id,
                      eventValue($event)
                    )
                  "
                />
                <input
                  v-else
                  :checked="Boolean(application.propertyValues[property.id])"
                  type="checkbox"
                  @change="
                    setApplicationValue(
                      application.id,
                      property.id,
                      eventChecked($event)
                    )
                  "
                />
              </td>
              <td class="actions">
                <button
                  type="button"
                  class="danger"
                  @click="removeApplication(application.id)"
                >
                  Delete
                </button>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <div class="card">
        <h2>Application Properties</h2>
        <table class="data-table">
          <thead>
            <tr>
              <th>Name</th>
              <th>Type</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="property in sortedApplicationProperties"
              :key="property.id"
            >
              <td>{{ property.name }}</td>
              <td>{{ property.type }}</td>
              <td class="actions">
                <button
                  type="button"
                  class="ghost"
                  @click="editApplicationProperty(property)"
                >
                  Edit
                </button>
                <button
                  type="button"
                  class="danger"
                  @click="removeApplicationProperty(property.id)"
                >
                  Delete
                </button>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </section>

    <section v-else-if="screen === 'docs'" class="card stack">
      <div class="split">
        <h2>Specification Reference</h2>
        <span class="muted"
          >Loaded from the repository documentation source</span
        >
      </div>
      <pre class="docs">{{ docsContent }}</pre>
    </section>
  </div>
</template>

<script lang="ts">
import { computed, defineComponent, PropType, reactive, ref, watch } from "vue";
import { useRouter } from "vue-router";
import type {
  ApplicationProperty,
  Branch,
  Customer,
  DeployUnit,
  FutureDeployNote,
  PlannedDeploy,
  PropertyType,
  Ring,
  Section,
  Step,
  User,
} from "@/store/customerManager";
import { useCustomerManager } from "@/store/customerManager";

export default defineComponent({
  name: "WorkspaceView",
  props: {
    screen: {
      type: String as PropType<string>,
      required: true,
    },
  },
  setup(props) {
    const router = useRouter();
    const manager = useCustomerManager();
    const { state } = manager;

    const userForm = reactive({
      id: "",
      name: "",
      surname: "",
      email: "",
    });
    const deployUnitForm = reactive({
      id: "",
      name: "",
      description: "",
    });
    const sectionForm = reactive({
      id: "",
      name: "",
      deployUnitId: "",
      position: 1,
    });
    const stepForm = reactive({
      id: "",
      name: "",
      description: "",
      sectionId: "",
      position: 1,
    });
    const ringForm = reactive({
      id: "",
      name: "",
      description: "",
      color: "#2563eb",
      order: 1,
    });
    const customerForm = reactive({
      id: "",
      name: "",
      currentVersion: "",
      lastDeployDate: "",
      deployedBy: "",
      ringId: "",
      deployUnitIds: [] as string[],
      liveDate: "",
      dbValidDate: "",
    });
    const noteForm = reactive({
      id: "",
      title: "",
      content: "",
      customerName: "",
      targetVersion: "",
      deployUnitId: "",
    });
    const plannedForm = reactive({
      id: "",
      customerName: "",
      currentVersion: "",
      targetVersion: "",
      plannedDate: "",
      assignedUserId: "",
      notes: "",
      status: "planned" as
        | "planned"
        | "in-progress"
        | "completed"
        | "cancelled",
    });
    const deployForm = reactive({
      customerId: "",
      currentVersion: "",
      targetVersion: "",
      userId: "",
      branchId: "",
    });
    const branchForm = reactive({
      id: "",
      name: "",
      startingIteration: 0,
    });
    const applicationPropertyForm = reactive({
      id: "",
      name: "",
      type: "text" as PropertyType,
    });
    const applicationName = ref("");

    const customerSearch = ref("");
    const historySearch = ref("");
    const historyStatusFilter = ref("all");
    const docsContent = ref("Loading documentation...");
    const ringAssignmentTargetId = ref("");
    const ringAssignmentCustomerIds = ref<string[]>([]);
    const activeReminderId = ref("");
    const reminderUserIds = ref<string[]>([]);
    const reminderExtraEmails = ref("");
    const reminderMessage = ref("");

    const sortedUsers = computed(() =>
      [...state.data.users].sort((left, right) =>
        `${left.name} ${left.surname}`.localeCompare(
          `${right.name} ${right.surname}`
        )
      )
    );
    const sortedDeployUnits = computed(() =>
      [...state.data.deployUnits].sort((left, right) =>
        left.name.localeCompare(right.name)
      )
    );
    const sortedSections = computed(() =>
      [...state.data.sections].sort(
        (left, right) =>
          left.deployUnitId.localeCompare(right.deployUnitId) ||
          left.position - right.position
      )
    );
    const orderedRings = computed(() =>
      [...state.data.rings].sort((left, right) => left.order - right.order)
    );
    const sortedCustomers = computed(() =>
      [...state.data.customers].sort((left, right) =>
        left.name.localeCompare(right.name)
      )
    );
    const sortedNotes = computed(() =>
      [...state.data.futureDeployNotes].sort((left, right) =>
        right.createdAt.localeCompare(left.createdAt)
      )
    );
    const sortedPlannedDeploys = computed(() =>
      manager.sortByDateAscendingWithUndatedLast(state.data.plannedDeploys)
    );
    const sortedBranches = computed(() =>
      [...state.data.branches].sort((left, right) =>
        left.name.localeCompare(right.name)
      )
    );
    const sortedApplications = computed(() =>
      [...state.data.applications].sort((left, right) =>
        left.name.localeCompare(right.name)
      )
    );
    const sortedApplicationProperties = computed(() =>
      [...state.data.applicationProperties].sort((left, right) =>
        left.createdAt.localeCompare(right.createdAt)
      )
    );

    const customerVersionBuckets = computed(() => {
      const buckets = new Map<string, Customer[]>();
      sortedCustomers.value.forEach((customer) => {
        const version = customer.currentVersion || "Unknown";
        const existing = buckets.get(version) ?? [];
        existing.push(customer);
        buckets.set(version, existing);
      });
      return Array.from(buckets.entries())
        .sort(([left], [right]) => {
          if (left === "Unknown") {
            return 1;
          }
          if (right === "Unknown") {
            return -1;
          }
          return manager.compareVersions(left, right);
        })
        .map(([version, customers]) => ({ version, customers }));
    });

    const nextRingRecommendation = computed(() => {
      const todayValue = new Date();
      const rings = orderedRings.value
        .map((ring) => ({
          ring,
          customers: sortedCustomers.value
            .filter((customer) => customer.ringId === ring.id)
            .map((customer) => {
              if (!customer.lastDeployDate) {
                return { ...customer, daysSinceDeploy: "Never deployed" };
              }
              const diff = Math.floor(
                (todayValue.getTime() -
                  new Date(customer.lastDeployDate).getTime()) /
                  (24 * 60 * 60 * 1000)
              );
              return { ...customer, daysSinceDeploy: `${diff} days` };
            }),
        }))
        .filter((entry) => entry.customers.length > 0);
      if (!rings.length) {
        return null;
      }
      return (
        rings.find((entry) =>
          entry.customers.some(
            (customer) =>
              customer.daysSinceDeploy === "Never deployed" ||
              Number.parseInt(customer.daysSinceDeploy, 10) > 30
          )
        ) ?? rings[0]
      );
    });

    const upcomingPlanned = computed(() =>
      sortedPlannedDeploys.value
        .filter((planned) => planned.status === "planned")
        .slice(0, 5)
    );

    const customerRows = computed(() =>
      manager.getCustomerOverview(customerSearch.value)
    );
    const filteredHistoryRows = computed(() =>
      manager.getHistoryRows(historySearch.value, historyStatusFilter.value)
    );
    const selectedHistoryDeployment = computed(() => {
      const targetId = state.ui.selectedHistoryDeploymentId;
      return (
        filteredHistoryRows.value.find(
          (deployment) => deployment.id === targetId
        ) ??
        filteredHistoryRows.value[0] ??
        null
      );
    });
    const activeDeployment = computed(
      () =>
        state.data.deployments.find(
          (deployment) => deployment.id === state.ui.activeDeploymentId
        ) ?? null
    );
    const activeDeploymentProgress = computed(() =>
      activeDeployment.value
        ? manager.getProgress(activeDeployment.value)
        : { completed: 0, total: 0, percentage: 0, status: "not-started" }
    );
    const activeDeploymentGroups = computed(() =>
      activeDeployment.value
        ? manager.getDeploymentGroups(activeDeployment.value)
        : []
    );
    const selectedDeployCustomer = computed(
      () =>
        state.data.customers.find(
          (customer) => customer.id === deployForm.customerId
        ) ?? null
    );
    const deployPreviewStepCount = computed(() => {
      if (!selectedDeployCustomer.value) {
        return 0;
      }
      const sectionIds = state.data.sections
        .filter((section) =>
          selectedDeployCustomer.value?.deployUnitIds.includes(
            section.deployUnitId
          )
        )
        .map((section) => section.id);
      return state.data.steps.filter((step) =>
        sectionIds.includes(step.sectionId)
      ).length;
    });
    const deployStartNotes = computed(() =>
      manager.getMatchingNotes({
        customerName: selectedDeployCustomer.value?.name ?? "",
        targetVersion: deployForm.targetVersion,
        deployUnitIds: selectedDeployCustomer.value?.deployUnitIds ?? [],
      })
    );
    const canStartDeployment = computed(() =>
      Boolean(
        deployForm.customerId &&
          deployForm.currentVersion &&
          deployForm.targetVersion &&
          deployForm.userId &&
          state.data.users.length &&
          state.data.customers.length &&
          state.data.steps.length
      )
    );
    const activeReminderPlanned = computed(
      () =>
        state.data.plannedDeploys.find(
          (planned) => planned.id === activeReminderId.value
        ) ?? null
    );

    const resetUserForm = () => {
      Object.assign(userForm, {
        id: "",
        name: "",
        surname: "",
        email: "",
      });
    };
    const resetDeployUnitForm = () => {
      Object.assign(deployUnitForm, {
        id: "",
        name: "",
        description: "",
      });
    };
    const resetSectionForm = () => {
      Object.assign(sectionForm, {
        id: "",
        name: "",
        deployUnitId: sortedDeployUnits.value[0]?.id ?? "",
        position: 1,
      });
    };
    const resetStepForm = () => {
      Object.assign(stepForm, {
        id: "",
        name: "",
        description: "",
        sectionId: sortedSections.value[0]?.id ?? "",
        position: 1,
      });
    };
    const resetRingForm = () => {
      Object.assign(ringForm, {
        id: "",
        name: "",
        description: "",
        color: "#2563eb",
        order: orderedRings.value.length + 1,
      });
    };
    const resetCustomerForm = () => {
      Object.assign(customerForm, {
        id: "",
        name: "",
        currentVersion: "",
        lastDeployDate: "",
        deployedBy: "",
        ringId: "",
        deployUnitIds: [] as string[],
        liveDate: "",
        dbValidDate: "",
      });
    };
    const resetNoteForm = () => {
      Object.assign(noteForm, {
        id: "",
        title: "",
        content: "",
        customerName: "",
        targetVersion: "",
        deployUnitId: "",
      });
    };
    const resetPlannedForm = () => {
      Object.assign(plannedForm, {
        id: "",
        customerName: "",
        currentVersion: "",
        targetVersion: "",
        plannedDate: "",
        assignedUserId: "",
        notes: "",
        status: "planned",
      });
    };
    const resetDeployForm = () => {
      Object.assign(deployForm, {
        customerId: "",
        currentVersion: "",
        targetVersion: "",
        userId: "",
        branchId: "",
      });
    };
    const resetBranchForm = () => {
      Object.assign(branchForm, {
        id: "",
        name: "",
        startingIteration: 0,
      });
    };
    const resetApplicationPropertyForm = () => {
      Object.assign(applicationPropertyForm, {
        id: "",
        name: "",
        type: "text",
      });
    };

    const submitUser = () => {
      if (userForm.id) {
        manager.updateUser(userForm.id, {
          name: userForm.name,
          surname: userForm.surname,
          email: userForm.email,
        });
      } else {
        manager.createUser({
          name: userForm.name,
          surname: userForm.surname,
          email: userForm.email,
        });
      }
      resetUserForm();
    };
    const editUser = (user: User) => {
      Object.assign(userForm, user);
    };
    const removeUser = (id: string) => {
      if (window.confirm("Delete this user?")) {
        manager.deleteUser(id);
      }
    };

    const submitDeployUnit = () => {
      const payload = {
        name: deployUnitForm.name,
        description: deployUnitForm.description,
      };
      if (deployUnitForm.id) {
        manager.updateDeployUnit(deployUnitForm.id, payload);
      } else {
        manager.createDeployUnit(payload);
      }
      resetDeployUnitForm();
    };
    const editDeployUnit = (unit: DeployUnit) => {
      Object.assign(deployUnitForm, unit);
    };
    const removeDeployUnit = (id: string) => {
      if (
        window.confirm("Delete this deploy unit and its sections and steps?")
      ) {
        manager.deleteDeployUnit(id);
      }
    };

    const getSectionsForUnit = (deployUnitId: string) =>
      [...state.data.sections]
        .filter((section) => section.deployUnitId === deployUnitId)
        .sort((left, right) => left.position - right.position);
    const getStepsForSection = (sectionId: string) =>
      [...state.data.steps]
        .filter((step) => step.sectionId === sectionId)
        .sort((left, right) => left.position - right.position);
    const primeSectionForUnit = (deployUnitId: string) => {
      Object.assign(sectionForm, {
        id: "",
        name: "",
        deployUnitId,
        position: getSectionsForUnit(deployUnitId).length + 1,
      });
    };
    const primeStepForSection = (sectionId: string) => {
      Object.assign(stepForm, {
        id: "",
        name: "",
        description: "",
        sectionId,
        position: getStepsForSection(sectionId).length + 1,
      });
    };
    const primeStepAfter = (step: Step) => {
      Object.assign(stepForm, {
        id: "",
        name: "",
        description: "",
        sectionId: step.sectionId,
        position: step.position + 1,
      });
    };
    const submitSection = () => {
      const payload = {
        name: sectionForm.name,
        deployUnitId: sectionForm.deployUnitId,
        position: sectionForm.position,
      };
      if (sectionForm.id) {
        manager.updateSection(sectionForm.id, payload);
      } else {
        manager.createSection(payload);
      }
      resetSectionForm();
    };
    const editSection = (section: Section) => {
      Object.assign(sectionForm, section);
    };
    const removeSection = (id: string) => {
      if (window.confirm("Delete this section?")) {
        manager.deleteSection(id);
      }
    };
    const submitStep = () => {
      const payload = {
        name: stepForm.name,
        description: stepForm.description,
        sectionId: stepForm.sectionId,
        position: stepForm.position,
      };
      if (stepForm.id) {
        manager.updateStep(stepForm.id, payload);
      } else {
        manager.createStep(payload);
      }
      resetStepForm();
    };
    const editStep = (step: Step) => {
      Object.assign(stepForm, step);
    };
    const removeStep = (id: string) => {
      if (window.confirm("Delete this step?")) {
        manager.deleteStep(id);
      }
    };

    const submitRing = () => {
      const payload = {
        name: ringForm.name,
        description: ringForm.description,
        color: ringForm.color,
        order: ringForm.order,
      };
      if (ringForm.id) {
        manager.updateRing(ringForm.id, payload);
      } else {
        manager.createRing(payload);
      }
      resetRingForm();
    };
    const editRing = (ring: Ring) => {
      Object.assign(ringForm, ring);
    };
    const removeRing = (id: string) => {
      if (window.confirm("Delete this ring?")) {
        manager.deleteRing(id);
      }
    };
    const openRingAssignment = (ringId: string) => {
      ringAssignmentTargetId.value = ringId;
      ringAssignmentCustomerIds.value = getCustomersForRing(ringId).map(
        (customer) => customer.id
      );
    };
    const cancelRingAssignment = () => {
      ringAssignmentTargetId.value = "";
      ringAssignmentCustomerIds.value = [];
    };
    const applyRingAssignment = (ringId: string) => {
      manager.assignCustomersToRing(ringId, ringAssignmentCustomerIds.value);
      cancelRingAssignment();
    };
    const moveRing = (id: string, direction: number) => {
      manager.moveRing(id, direction);
    };
    const getCustomersForRing = (ringId: string) =>
      sortedCustomers.value.filter((customer) => customer.ringId === ringId);
    const removeCustomerFromRing = (customerId: string) => {
      manager.removeCustomerFromRing(customerId);
    };

    const submitCustomer = () => {
      const payload = {
        name: customerForm.name,
        currentVersion: customerForm.currentVersion,
        lastDeployDate: customerForm.lastDeployDate,
        deployedBy: customerForm.deployedBy,
        ringId: customerForm.ringId || null,
        deployUnitIds: [...customerForm.deployUnitIds],
        liveDate: customerForm.liveDate,
        dbValidDate: customerForm.dbValidDate,
      };
      if (customerForm.id) {
        manager.updateCustomer(customerForm.id, payload);
      } else {
        manager.createCustomer(payload);
      }
      resetCustomerForm();
    };
    const editCustomer = (customer: Customer) => {
      Object.assign(customerForm, {
        ...customer,
        ringId: customer.ringId ?? "",
        deployUnitIds: [...customer.deployUnitIds],
      });
    };
    const removeCustomer = (id: string) => {
      if (window.confirm("Delete this customer?")) {
        manager.deleteCustomer(id);
      }
    };

    const submitNote = () => {
      const payload = {
        title: noteForm.title,
        content: noteForm.content,
        customerName: noteForm.customerName,
        targetVersion: noteForm.targetVersion,
        deployUnitId: noteForm.deployUnitId,
      };
      if (noteForm.id) {
        manager.updateNote(noteForm.id, payload);
      } else {
        manager.createNote(payload);
      }
      resetNoteForm();
    };
    const editNote = (note: FutureDeployNote) => {
      Object.assign(noteForm, note);
    };
    const removeNote = (id: string) => {
      if (window.confirm("Delete this note?")) {
        manager.deleteNote(id);
      }
    };

    const syncPlannedCurrentVersion = () => {
      const customer = state.data.customers.find(
        (candidate) => candidate.name === plannedForm.customerName
      );
      if (customer) {
        plannedForm.currentVersion = customer.currentVersion;
      }
    };
    const submitPlannedDeploy = () => {
      const payload = {
        customerName: plannedForm.customerName,
        currentVersion: plannedForm.currentVersion,
        targetVersion: plannedForm.targetVersion,
        plannedDate: plannedForm.plannedDate,
        assignedUserId: plannedForm.assignedUserId,
        notes: plannedForm.notes,
        status: plannedForm.status,
      };
      if (plannedForm.id) {
        manager.updatePlannedDeploy(plannedForm.id, payload);
      } else {
        manager.createPlannedDeploy(payload);
      }
      resetPlannedForm();
    };
    const editPlannedDeploy = (planned: PlannedDeploy) => {
      Object.assign(plannedForm, planned);
    };
    const removePlannedDeploy = (id: string) => {
      if (window.confirm("Delete this planned deploy?")) {
        manager.deletePlannedDeploy(id);
      }
    };
    const markPlannedCompleted = (id: string) => {
      manager.setPlannedDeployStatus(id, "completed");
    };
    const markPlannedCancelled = (id: string) => {
      manager.setPlannedDeployStatus(id, "cancelled");
    };
    const reopenPlanned = (id: string) => {
      manager.setPlannedDeployStatus(id, "planned");
    };
    const startPlanned = (id: string) => {
      if (manager.startPlannedDeploy(id)) {
        router.push("/deploy");
      }
    };
    const openReminder = (id: string) => {
      activeReminderId.value = id;
      reminderUserIds.value = [];
      reminderExtraEmails.value = "";
      reminderMessage.value = "";
    };
    const closeReminder = () => {
      activeReminderId.value = "";
      reminderUserIds.value = [];
      reminderExtraEmails.value = "";
      reminderMessage.value = "";
    };
    const sendReminder = () => {
      const extraEmails = reminderExtraEmails.value
        .split(",")
        .map((email) => email.trim())
        .filter(Boolean);
      const recipients = [
        ...reminderUserIds.value.map((id) => manager.getUserLabel(id)),
        ...extraEmails,
      ];
      manager.sendReminder(reminderMessage.value, recipients);
      closeReminder();
    };

    const syncDeployCurrentVersion = () => {
      if (selectedDeployCustomer.value) {
        deployForm.currentVersion = selectedDeployCustomer.value.currentVersion;
      }
    };
    const submitDeployment = () => {
      if (
        manager.startDeployment({
          customerId: deployForm.customerId,
          currentVersion: deployForm.currentVersion,
          targetVersion: deployForm.targetVersion,
          userId: deployForm.userId,
          branchId: deployForm.branchId,
        })
      ) {
        resetDeployForm();
      }
    };
    const leaveActiveDeployment = () => {
      manager.setActiveDeployment(null);
    };
    const getUnitNotesForDeployment = (groupKey: string) => {
      if (!activeDeployment.value) {
        return [];
      }
      const unitId =
        state.data.deployUnits.find((unit) => unit.id === groupKey)?.id ?? "";
      const customer = state.data.customers.find(
        (entry) => entry.name === activeDeployment.value?.customerName
      );
      return manager
        .getMatchingNotes({
          customerName: activeDeployment.value.customerName,
          targetVersion: activeDeployment.value.targetVersion,
          deployUnitIds: customer?.deployUnitIds ?? [],
        })
        .filter((note) => !note.deployUnitId || note.deployUnitId === unitId);
    };
    const toggleDeploymentStep = (
      deploymentId: string,
      stepId: string,
      completed: boolean
    ) => {
      manager.toggleDeploymentStep(deploymentId, stepId, completed);
    };
    const updateDeploymentStepNotes = (
      deploymentId: string,
      stepId: string,
      notes: string
    ) => {
      manager.updateDeploymentStepNotes(deploymentId, stepId, notes);
    };

    const selectHistoryDeployment = (deploymentId: string) => {
      manager.setHistorySelection(deploymentId);
    };
    const openHistoryDeployment = (deploymentId: string) => {
      manager.setHistorySelection(deploymentId);
      router.push("/history");
    };
    const resumeDeployment = (deploymentId: string) => {
      manager.setHistorySelection(deploymentId);
      manager.setActiveDeployment(deploymentId);
      router.push("/deploy");
    };
    const exportFilteredHistoryCsv = () => {
      manager.exportFilteredDeploymentsCsv(filteredHistoryRows.value);
    };

    const submitBranch = () => {
      if (branchForm.id) {
        manager.updateBranch(branchForm.id, {
          name: branchForm.name,
          startingIteration: branchForm.startingIteration,
        });
      } else {
        manager.createBranch({
          name: branchForm.name,
          startingIteration: branchForm.startingIteration,
        });
      }
      resetBranchForm();
    };
    const editBranch = (branch: Branch) => {
      Object.assign(branchForm, branch);
    };
    const removeBranch = (id: string) => {
      if (window.confirm("Delete this branch?")) {
        manager.deleteBranch(id);
      }
    };

    const submitApplication = () => {
      manager.createApplication(applicationName.value);
      applicationName.value = "";
    };
    const removeApplication = (id: string) => {
      if (window.confirm("Delete this application?")) {
        manager.deleteApplication(id);
      }
    };
    const renameApplication = (id: string, name: string) => {
      manager.renameApplication(id, name);
    };
    const setApplicationValue = (
      applicationId: string,
      propertyId: string,
      value: string | boolean
    ) => {
      manager.setApplicationValue(applicationId, propertyId, value);
    };
    const eventValue = (event: Event): string => {
      const target = event.target as
        | HTMLInputElement
        | HTMLTextAreaElement
        | null;
      return target?.value ?? "";
    };
    const eventChecked = (event: Event): boolean => {
      const target = event.target as HTMLInputElement | null;
      return Boolean(target?.checked);
    };
    const submitApplicationProperty = () => {
      if (applicationPropertyForm.id) {
        manager.updateApplicationProperty(applicationPropertyForm.id, {
          name: applicationPropertyForm.name,
          type: applicationPropertyForm.type,
        });
      } else {
        manager.createApplicationProperty({
          name: applicationPropertyForm.name,
          type: applicationPropertyForm.type,
        });
      }
      resetApplicationPropertyForm();
    };
    const editApplicationProperty = (property: ApplicationProperty) => {
      Object.assign(applicationPropertyForm, property);
    };
    const removeApplicationProperty = (id: string) => {
      if (window.confirm("Delete this property from every application?")) {
        manager.deleteApplicationProperty(id);
      }
    };

    watch(
      () => state.ui.plannedHandoff,
      (handoff) => {
        if (!handoff || state.ui.activeDeploymentId) {
          return;
        }
        const customer = state.data.customers.find(
          (candidate) => candidate.name === handoff.customerName
        );
        Object.assign(deployForm, {
          customerId: customer?.id ?? "",
          currentVersion: handoff.currentVersion,
          targetVersion: handoff.targetVersion,
          userId: handoff.userId,
          branchId: "",
        });
      },
      { immediate: true, deep: true }
    );

    watch(
      () => props.screen,
      async (screen) => {
        if (screen === "docs") {
          docsContent.value = await manager.loadDocs();
        }
      },
      { immediate: true }
    );

    resetSectionForm();
    resetStepForm();
    resetRingForm();

    return {
      state,
      userForm,
      deployUnitForm,
      sectionForm,
      stepForm,
      ringForm,
      customerForm,
      noteForm,
      plannedForm,
      deployForm,
      branchForm,
      applicationPropertyForm,
      applicationName,
      customerSearch,
      historySearch,
      historyStatusFilter,
      docsContent,
      ringAssignmentTargetId,
      ringAssignmentCustomerIds,
      reminderUserIds,
      reminderExtraEmails,
      reminderMessage,
      activeReminderPlanned,
      sortedUsers,
      sortedDeployUnits,
      sortedSections,
      orderedRings,
      sortedCustomers,
      sortedNotes,
      sortedPlannedDeploys,
      sortedBranches,
      sortedApplications,
      sortedApplicationProperties,
      customerVersionBuckets,
      nextRingRecommendation,
      upcomingPlanned,
      customerRows,
      filteredHistoryRows,
      selectedHistoryDeployment,
      activeDeployment,
      activeDeploymentProgress,
      activeDeploymentGroups,
      deployPreviewStepCount,
      deployStartNotes,
      canStartDeployment,
      getUserLabel: manager.getUserLabel,
      getRingLabel: manager.getRingLabel,
      getDeployUnitLabel: manager.getDeployUnitLabel,
      getProgress: manager.getProgress,
      getDeploymentGroups: manager.getDeploymentGroups,
      getSectionsForUnit,
      getStepsForSection,
      getCustomersForRing,
      resetUserForm,
      resetDeployUnitForm,
      resetSectionForm,
      resetStepForm,
      resetRingForm,
      resetCustomerForm,
      resetNoteForm,
      resetPlannedForm,
      resetBranchForm,
      resetApplicationPropertyForm,
      submitUser,
      editUser,
      removeUser,
      submitDeployUnit,
      editDeployUnit,
      removeDeployUnit,
      submitSection,
      editSection,
      removeSection,
      primeSectionForUnit,
      submitStep,
      editStep,
      removeStep,
      primeStepForSection,
      primeStepAfter,
      submitRing,
      editRing,
      removeRing,
      openRingAssignment,
      cancelRingAssignment,
      applyRingAssignment,
      moveRing,
      removeCustomerFromRing,
      submitCustomer,
      editCustomer,
      removeCustomer,
      submitNote,
      editNote,
      removeNote,
      submitPlannedDeploy,
      editPlannedDeploy,
      removePlannedDeploy,
      markPlannedCompleted,
      markPlannedCancelled,
      reopenPlanned,
      startPlanned,
      openReminder,
      closeReminder,
      sendReminder,
      syncPlannedCurrentVersion,
      syncDeployCurrentVersion,
      submitDeployment,
      leaveActiveDeployment,
      getUnitNotesForDeployment,
      toggleDeploymentStep,
      updateDeploymentStepNotes,
      selectHistoryDeployment,
      openHistoryDeployment,
      resumeDeployment,
      exportFilteredHistoryCsv,
      exportDeploymentCsv: manager.exportDeploymentCsv,
      printDeployment: manager.printDeployment,
      submitBranch,
      editBranch,
      removeBranch,
      submitApplication,
      removeApplication,
      renameApplication,
      setApplicationValue,
      eventValue,
      eventChecked,
      submitApplicationProperty,
      editApplicationProperty,
      removeApplicationProperty,
    };
  },
});
</script>

<style scoped lang="scss">
.screen {
  display: grid;
  gap: 16px;
}

.stack {
  display: grid;
  gap: 16px;
}

.two-column {
  display: grid;
  grid-template-columns: minmax(0, 360px) minmax(0, 1fr);
  gap: 16px;
}

.three-column {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 16px;
}

.responsive-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
  gap: 16px;
}

.card,
.soft-card {
  background: #fff;
  border-radius: 16px;
  padding: 18px;
  box-shadow: 0 12px 30px rgba(15, 23, 42, 0.08);
}

.soft-card {
  background: #f8fbff;
  box-shadow: none;
  border: 1px solid #e2e8f0;
}

.split {
  display: flex;
  justify-content: space-between;
  gap: 12px;
  align-items: center;
}

.muted {
  color: #64748b;
}

.simple-list {
  display: grid;
  gap: 8px;
  padding-left: 18px;
  margin: 0;
}

.simple-list li {
  display: flex;
  justify-content: space-between;
  gap: 12px;
  align-items: flex-start;
}

.data-table {
  width: 100%;
  border-collapse: collapse;
}

.data-table th,
.data-table td {
  padding: 10px 12px;
  border-bottom: 1px solid #e2e8f0;
  text-align: left;
  vertical-align: top;
}

.actions {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.details-panel {
  border: 1px solid #dbe4ef;
  border-radius: 12px;
  padding: 14px;
}

.details-panel--nested {
  background: #fbfdff;
}

.details-panel summary {
  display: flex;
  justify-content: space-between;
  gap: 12px;
  align-items: center;
  cursor: pointer;
  font-weight: 600;
}

label {
  display: grid;
  gap: 6px;
  font-weight: 600;
}

input,
select,
textarea {
  width: 100%;
  border: 1px solid #cbd5e1;
  border-radius: 10px;
  padding: 10px 12px;
  background: #fff;
}

select[multiple] {
  min-height: 140px;
}

.badge {
  display: inline-flex;
  align-items: center;
  border-radius: 999px;
  padding: 6px 10px;
  color: #fff;
  font-size: 0.85rem;
}

.status-pill {
  display: inline-flex;
  border-radius: 999px;
  background: #dbeafe;
  color: #1d4ed8;
  padding: 6px 10px;
}

.badge-list {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.badge-list__item {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  border-radius: 999px;
  padding: 6px 10px;
  background: #e2e8f0;
}

.badge-list__item button {
  background: transparent;
  color: #334155;
  padding: 0;
}

.color-dot {
  width: 18px;
  height: 18px;
  border-radius: 50%;
}

.assignment-box {
  padding-top: 8px;
  border-top: 1px solid #e2e8f0;
}

.checkbox-row {
  display: flex;
  gap: 10px;
  align-items: flex-start;
  font-weight: 400;
}

.checkbox-row input {
  width: auto;
  margin-top: 4px;
}

.note-card p {
  margin: 0;
  white-space: pre-wrap;
}

.step-row {
  display: grid;
  gap: 10px;
  padding: 12px 0;
  border-bottom: 1px solid #e2e8f0;
}

.step-row:last-child {
  border-bottom: none;
}

.success-callout {
  margin: 0;
  border-radius: 12px;
  padding: 14px 16px;
  background: #dcfce7;
  color: #166534;
}

.docs {
  margin: 0;
  white-space: pre-wrap;
  overflow: auto;
  background: #0f172a;
  color: #e2e8f0;
  border-radius: 12px;
  padding: 18px;
}

@media (max-width: 1100px) {
  .two-column,
  .three-column {
    grid-template-columns: 1fr;
  }
}
</style>
